/**
 * An owner's own sale, decided: whether each step may be left, what the
 * read-back says, and the request that records it — OND-173.
 *
 * Pure. A draft goes in; a problem, a summary or a wire body comes out. The
 * copy is `ownerSale.constants.ts`; the one write and the gate on whether the
 * control may be drawn at all are `useOwnerSaleController.ts`; the panel and
 * the dialog are `OwnerSalePanel.tsx` and `OwnerSaleDialog.tsx`.
 *
 * THE GALLERY'S SALE IS `gallery/sellWizard.model.ts` AND THIS IS NOT A COPY OF
 * IT. What the two share is lifted rather than duplicated — `parseMoneyMinor`
 * from `lib/money.ts`, `namedBuyer` and `BuyerMode` from `lib/buyer.ts`, both
 * of which that module now re-exports. What is NOT shared is everything
 * 20261014093000 decided differently, and each difference carries its comment
 * where it sits: no consignment to read terms off, no covenant, no fee, two
 * channels rather than three, no buyer check before submit, and a date of sale
 * the gallery's form does not offer.
 *
 * WHAT THE SERVER DECIDES, THIS FILE DOES NOT RESTATE. Whether the caller owns
 * the work, whether a gallery holds an accepted consignment on it, whether an
 * earlier sale is still awaiting title, whether a covenant locks it, and
 * whether the buyer's id names anybody this work can move to are all answered
 * inside `ondo.effect_owner_sale()`'s engine and arrive as errors to render.
 * What is here is only what the FORM must know to be usable.
 */

import type { EffectOwnerSaleRequest, TransferChannel, VatScheme } from "@ondo/shared";
import { formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { namedBuyer } from "../lib/buyer.ts";
import type { BuyerMode, NamedBuyer } from "../lib/buyer.ts";
import { formatDay } from "../lib/dates.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import { OWNER_SALE_COPY } from "./ownerSale.constants.ts";

/**
 * TWO STEPS, where the gallery's wizard has five, and the three that are gone
 * are gone for a reason rather than for brevity.
 *
 * `consignment` has no subject — there is no row to read terms back off, which
 * is the whole of this authority. `covenant` is 20261014093000 decision 8: the
 * door has no covenant argument at all. `fee` is decisions 6 and 7: the fee
 * follows the firm, and no firm effected this sale.
 *
 * A STEP UNION, NOT TWO URLS, for `sellWizard.model.ts`'s own reason: arriving
 * at the last step by typing is arriving with no buyer and no price, and the
 * one thing a URL is for is being arrived at out of order.
 */
export type OwnerSaleStep = "terms" | "record";

/** In order. The stepper renders this, and `nextStep` walks it. */
export const OWNER_SALE_STEPS: readonly OwnerSaleStep[] = ["terms", "record"];

/** The two channels the engine will accept here. Narrower than `TransferChannel`. */
export type OwnerSaleChannel = Extract<TransferChannel, "private" | "auction">;

/**
 * The form as it accumulates.
 *
 * Money and VAT live here as TYPED TEXT and become minor units only at the
 * boundary, which is `EffectOwnerSaleRequest.priceMinor`'s own requirement:
 * "The client parses what was typed; the server never sees text." The date
 * lives as the `yyyy-mm-dd` a `<input type="date">` yields, and an empty string
 * means "now" rather than a date of its own.
 */
export interface OwnerSaleDraft {
  buyerMode: BuyerMode;
  buyerName: string;
  buyerOndoId: string;
  /**
   * The address an off-ONDO buyer is reachable at. ONLY ever sent with a name.
   *
   * `ondo.effect_sale_authorised()` refuses an address beside an ONDO id —
   * "a buyer named by ONDO id is already here" — so this is read only on the
   * name arm, the same way `buyerMode` decides which buyer field is read.
   */
  buyerEmail: string;
  /** As typed. `parseMoneyMinor` is the only thing that reads it. */
  price: string;
  currency: string;
  channel: OwnerSaleChannel;
  addVat: boolean;
  vatScheme: VatScheme;
  vat: string;
  /** `yyyy-mm-dd`, or '' for a sale being recorded as it happens. */
  soldOn: string;
  /** The last step's one gate. A statement the owner is making, not a validation. */
  confirmedTitle: boolean;
}

/**
 * The draft a work opens with.
 *
 * EVERY FIELD EMPTY, which is the one place this is simpler than the gallery's
 * and not merely different. `initialDraft` there prefills the price and
 * currency off the consignment, because those are terms both sides already
 * agreed and a blank field would make a director retype what the registry
 * holds. An owner's sale has no such row: there is nothing on this work that
 * could honestly guess what it sold for, so the currency takes a default and
 * the figure is asked for.
 *
 * `channel` is `'private'`, which is both the wire's default and what a
 * collector selling to a collector is.
 */
export function initialOwnerSaleDraft(): OwnerSaleDraft {
  return {
    buyerMode: "ondo",
    buyerName: "",
    buyerOndoId: "",
    buyerEmail: "",
    price: "",
    currency: OWNER_SALE_COPY.defaultCurrency,
    channel: "private",
    addVat: false,
    vatScheme: "none",
    vat: "",
    soldOn: "",
    confirmedTitle: false,
  };
}

/** The buyer this draft names, or null. Re-exported so the view and the controller agree. */
export { namedBuyer };
export type { NamedBuyer };

/**
 * An ISO instant for a named day, or null when the field is not a day.
 *
 * MIDNIGHT UTC, deliberately, and the reason is that it is read back the same
 * way: `formatDay` has a `utc` option for exactly this, and the registry's
 * notion of a day has to be the one the seller typed rather than one that
 * shifts by where they were sitting. A sale's `sold_at` is a `timestamptz` and
 * an owner recording "14 October" means the day and not a moment in it.
 *
 * `''` IS NOT A DATE AND IS NOT AN ERROR: it is the answer "as it happens",
 * which `toEffectOwnerSaleRequest` turns into an absent key so the engine's own
 * `now()` applies. `TransfersService.effectOwnerSale` only forwards `p_sold_at`
 * when the body carries one — an explicit null would override the default
 * rather than take it.
 */
export function soldAtInstant(soldOn: string): string | null {
  const typed = soldOn.trim();
  if (typed === "") return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(typed)) return null;

  const at = new Date(`${typed}T00:00:00.000Z`);
  if (Number.isNaN(at.getTime())) return null;
  // A browser will parse "2026-02-31" into 3 March. The round trip catches it,
  // which is cheaper than month arithmetic and cannot disagree with the parser
  // the value is about to be sent through.
  if (at.toISOString().slice(0, 10) !== typed) return null;

  return at.toISOString();
}

/**
 * What stops this step being left, or null.
 *
 * A SENTENCE RATHER THAN A BOOLEAN, which is where this departs from
 * `canContinue` in the gallery's wizard. That one answers yes/no and greys
 * Continue out, which works there because every field on the gated step is
 * visible beside the button. Here the step carries a buyer toggle, a price, an
 * optional VAT block and an optional date, and a dead button with no reason
 * beside it is the state OND-173's own description calls out from the other
 * direction — something to press and no way to know why it will not go.
 *
 * NOTHING HERE ASKS ABOUT THE BUYER'S EXISTENCE. There is no `buyer-check` on
 * this route, so a well-shaped id that names nobody passes every gate in this
 * file and meets one opaque refusal at submit. That is the designed behaviour
 * and the reason `OWNER_SALE_COPY.idHelp` says so on the field.
 */
export function problemWith(step: OwnerSaleStep, draft: OwnerSaleDraft): string | null {
  if (step === "record") {
    return draft.confirmedTitle ? null : OWNER_SALE_COPY.confirm;
  }

  const buyer = namedBuyer(draft);
  if (buyer === null) {
    return draft.buyerMode === "ondo"
      ? OWNER_SALE_COPY.problems.buyerOndoId
      : OWNER_SALE_COPY.problems.buyerName;
  }

  // The address is read on the name arm only, so it can only be wrong there.
  // The shape is the server's own `z.string().email()`, approximated: anything
  // this accepts and Zod refuses arrives as the schema's own 422.
  if (buyer.kind === "name" && draft.buyerEmail.trim() !== "") {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.buyerEmail.trim())) {
      return OWNER_SALE_COPY.problems.buyerEmail;
    }
  }

  if (parseMoneyMinor(draft.price) === null) return OWNER_SALE_COPY.problems.price;
  if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) return OWNER_SALE_COPY.problems.currency;

  // Empty is not zero. The scheme may be recorded with no figure beside it, so
  // only a non-empty amount that does not parse is a problem.
  if (draft.addVat && draft.vat.trim() !== "" && parseMoneyMinor(draft.vat) === null) {
    return OWNER_SALE_COPY.problems.vat;
  }

  if (draft.soldOn.trim() !== "" && soldAtInstant(draft.soldOn) === null) {
    return OWNER_SALE_COPY.problems.soldOn;
  }

  return null;
}

/**
 * The draft as the wire takes it.
 *
 * Throws rather than returning a partial request: it is called behind
 * `problemWith(…) === null` on both steps, and a submit that silently dropped a
 * buyer would be the one bug in this file worth fearing. `sellWizard.model.ts`
 * makes the same choice for the same reason.
 *
 * NO COVENANT KEY, AND NOT A FALSE ONE EITHER. `EffectOwnerSaleRequest` has no
 * such field — 20261014093000 decision 8 is "expressed as an absent door rather
 * than a false flag", and the interface says so in its own doc comment.
 *
 * NO `soldAt` KEY when the field is empty, rather than `soldAt: null`. The
 * service forwards `p_sold_at` only when the body carries one, because a
 * Postgres default fires on an ABSENT argument and an explicit null overrides
 * it — the comment on `effectSale` records that as a shared trap.
 */
export function toEffectOwnerSaleRequest(draft: OwnerSaleDraft): EffectOwnerSaleRequest {
  const priceMinor = parseMoneyMinor(draft.price);
  const buyer = namedBuyer(draft);
  if (priceMinor === null || buyer === null) {
    throw new Error("The sale is not complete enough to send.");
  }

  const email = buyer.kind === "name" ? draft.buyerEmail.trim() : "";
  const soldAt = soldAtInstant(draft.soldOn);

  return {
    priceMinor,
    currency: draft.currency.trim().toUpperCase(),
    ...(buyer.kind === "ondo"
      ? { buyerOndoId: buyer.ondoId }
      : { buyerName: buyer.name, ...(email === "" ? {} : { buyerEmail: email }) }),
    channel: draft.channel,
    vatScheme: draft.addVat ? draft.vatScheme : "none",
    vatMinor: draft.addVat ? parseMoneyMinor(draft.vat) : null,
    ...(soldAt === null ? {} : { soldAt }),
  };
}

/** One row of the read-back, as the last step lists them. */
export interface OwnerSaleFact {
  label: string;
  value: string;
}

/** The price as the summary prints it. '' when it does not parse. */
export function describeDraftPrice(draft: OwnerSaleDraft): string {
  const minor = parseMoneyMinor(draft.price);
  if (minor === null) return "";

  return formatMoney({ minor, currency: draft.currency.trim().toUpperCase() });
}

/**
 * The last step's read-back, built from the same values the submit sends.
 *
 * NO FEE ROW AND NO COVENANT ROW, which `summaryRows` in the gallery's wizard
 * both carry. Decisions 6, 7 and 8: there is no fee on a sale no firm effected
 * and no covenant this door can attach, so a row saying "None" would still be
 * naming an instrument that is not on offer.
 *
 * The subject line is passed in rather than read off a row, because the panel
 * is mounted on the work's own record and already knows what work it is about.
 */
export function ownerSaleSummary(subject: string, draft: OwnerSaleDraft): OwnerSaleFact[] {
  const buyer = namedBuyer(draft);
  const copy = OWNER_SALE_COPY.summary;

  const rows: OwnerSaleFact[] = [
    { label: copy.work, value: subject },
    {
      label: copy.buyer,
      value: buyer === null ? "" : buyer.kind === "ondo" ? buyer.ondoId : buyer.name,
    },
  ];

  if (buyer?.kind === "name" && draft.buyerEmail.trim() !== "") {
    rows.push({ label: copy.buyerEmail, value: draft.buyerEmail.trim() });
  }

  rows.push(
    { label: copy.price, value: describeDraftPrice(draft) },
    { label: copy.channel, value: OWNER_SALE_COPY.channel[draft.channel] },
  );

  if (draft.addVat) {
    rows.push({ label: copy.vat, value: OWNER_SALE_COPY.vatScheme[draft.vatScheme] });
  }

  const soldAt = soldAtInstant(draft.soldOn);
  rows.push({
    label: copy.soldOn,
    // `utc` because `soldAtInstant` wrote midnight UTC: read back in the
    // reader's own zone, a day typed as the 14th would print as the 13th for
    // anybody west of Greenwich.
    value: soldAt === null ? copy.soldOnNow : formatDay(soldAt, { utc: true }),
  });

  return rows;
}

/** What the last step says the press will do — which depends on whether the buyer is on ONDO. */
export function describeEffect(draft: OwnerSaleDraft): string {
  return namedBuyer(draft)?.kind === "name"
    ? OWNER_SALE_COPY.effect.name
    : OWNER_SALE_COPY.effect.ondo;
}

/**
 * What went wrong, in words the seller can act on.
 *
 * PREFERS THE SERVER'S OWN SENTENCE, for `describeSaleError`'s reason and with
 * more force here: `ondo.effect_owner_sale()` reaches an engine that raises
 * several different 42501s, and only it knows which — a work consigned to a
 * gallery, a buyer id it will not resolve, a covenant from an earlier sale
 * locking the work, an identity standing that is not clear, and OND-17's 55000
 * about a work whose own earlier sale is still awaiting title. The screen can
 * predict none of them, and replacing any with a house sentence would tell the
 * seller the wrong thing about their own picture.
 */
export function describeOwnerSaleError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 429) return OWNER_SALE_COPY.errors.throttled;
    if (cause.status === 401) return OWNER_SALE_COPY.errors.signedOut;
    if (cause.message) return cause.message;

    return OWNER_SALE_COPY.errors.refused;
  }

  return cause instanceof Error ? cause.message : OWNER_SALE_COPY.errors.refused;
}

/** The step Continue goes to. Stays put on the last one, which is what a stray call does. */
export function nextStep(at: OwnerSaleStep): OwnerSaleStep {
  return OWNER_SALE_STEPS[
    Math.min(OWNER_SALE_STEPS.indexOf(at) + 1, OWNER_SALE_STEPS.length - 1)
  ]!;
}

/** The step Back goes to. Stays put on the first one, for the same reason. */
export function previousStep(at: OwnerSaleStep): OwnerSaleStep {
  return OWNER_SALE_STEPS[Math.max(OWNER_SALE_STEPS.indexOf(at) - 1, 0)]!;
}
